import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { cx, resolveDir } from './primitiveUtils';

/**
 * A one-of-N switch: للإيجار | للبيع, سكني | تجاري, العربية | English.
 * See Apps/web/THEME.md.
 *
 * - `role="radiogroup"` of radio buttons with a roving tabindex: Tab enters on the
 *   chosen option, arrow keys move AND choose (APG radio group). Left/Right are
 *   mirrored in RTL, so the arrow always moves the way the eye reads.
 * - `pill`: the chosen option is a teal pill that slides between options (the home
 *   hero's spring); reduced motion makes it jump.
 * - `underline`: teal text over a 2px red underline, the THEME register's "active
 *   nav underline". For tabs-like choices such as the hero's listing type.
 * - Sizes follow the proportion rule: sm = 0.875rem labels / 1rem icons,
 *   md = 0.9375rem / 1.125rem.
 * - `tone="onDark"` for a deep-teal surface (the home hero's search panel): white
 *   labels, the underline in the lifted red #FF8A80 (THEME.md §2.4, 5.29:1 on
 *   deep green), the chosen pill white with teal ink.
 */

export interface SegmentedOption<V extends string = string> {
  value: V;
  label: string;
  /** A remix-icon class ('ri-key-2-line') or a node; sized by the control. */
  icon?: ReactNode | string;
  disabled?: boolean;
}

export interface SegmentedControlProps<V extends string = string> {
  value: V;
  onChange: (value: V) => void;
  options: readonly SegmentedOption<V>[];
  /** Names the group for screen readers, e.g. «نوع العرض». */
  ariaLabel: string;
  variant?: 'pill' | 'underline';
  size?: 'sm' | 'md';
  /** Stretch to the container, options sharing the width equally. */
  fullWidth?: boolean;
  /** 'onDark' on a deep-teal surface; the default suits white and light surfaces. */
  tone?: 'default' | 'onDark';
  disabled?: boolean;
  id?: string;
  className?: string;
}

const SIZE = {
  sm: {
    // 2.25rem pill in a 2.75rem track; the ::before stretches the hit area over the
    // track's padding, so the touch target is the full 2.75rem.
    pill: "min-h-[2.25rem] px-4 text-[0.875rem] before:absolute before:inset-x-0 before:-inset-y-1 before:content-['']",
    underline: 'min-h-[2.75rem] px-3 text-[0.875rem]',
    icon: 'text-[1rem] [&_svg]:h-4 [&_svg]:w-4',
    gap: 'gap-1.5',
  },
  md: {
    pill: 'min-h-[2.75rem] px-5 text-[0.9375rem]',
    underline: 'min-h-[3rem] px-4 text-[0.9375rem]',
    icon: 'text-[1.125rem] [&_svg]:h-[1.125rem] [&_svg]:w-[1.125rem]',
    gap: 'gap-2',
  },
} as const;

const SPRING = { type: 'spring', stiffness: 420, damping: 34 } as const;

export default function SegmentedControl<V extends string = string>({
  value,
  onChange,
  options,
  ariaLabel,
  variant = 'pill',
  size = 'md',
  fullWidth = false,
  tone = 'default',
  disabled = false,
  id,
  className = '',
}: SegmentedControlProps<V>) {
  const onDark = tone === 'onDark';
  const groupId = useId();
  const reduce = useReducedMotion() ?? false;
  const buttonsRef = useRef<Array<HTMLButtonElement | null>>([]);

  const isOff = (i: number) => disabled || !!options[i]?.disabled;
  const checkedIndex = options.findIndex((o) => o.value === value);
  // Roving tabindex: the chosen option, or the first usable one when none is chosen.
  let tabStop = checkedIndex >= 0 && !isOff(checkedIndex) ? checkedIndex : -1;
  if (tabStop < 0) tabStop = options.findIndex((_, i) => !isOff(i));

  /** Choose `index`; keyboard moves also move focus with the choice (roving tabindex). */
  const select = (index: number, moveFocus: boolean) => {
    const option = options[index];
    if (!option || isOff(index)) return;
    if (moveFocus) buttonsRef.current[index]?.focus();
    if (option.value !== value) onChange(option.value);
  };

  /** Next usable index from `from`, wrapping, in direction `delta`. */
  const nextFrom = (from: number, delta: 1 | -1) => {
    const n = options.length;
    for (let step = 1; step <= n; step += 1) {
      const i = (((from + delta * step) % n) + n) % n;
      if (!isOff(i)) return i;
    }
    return from;
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const rtl = resolveDir(event.currentTarget) === 'rtl';
    let target: number | null = null;
    switch (event.key) {
      case 'ArrowRight':
        target = nextFrom(index, rtl ? -1 : 1);
        break;
      case 'ArrowLeft':
        target = nextFrom(index, rtl ? 1 : -1);
        break;
      case 'ArrowDown':
        target = nextFrom(index, 1);
        break;
      case 'ArrowUp':
        target = nextFrom(index, -1);
        break;
      case 'Home':
        target = options.findIndex((_, i) => !isOff(i));
        break;
      case 'End':
        for (let i = options.length - 1; i >= 0; i -= 1) {
          if (!isOff(i)) {
            target = i;
            break;
          }
        }
        break;
      default:
        return;
    }
    event.preventDefault();
    if (target !== null && target >= 0) select(target, true);
  };

  const s = SIZE[size];
  const isPill = variant === 'pill';

  return (
    <div
      id={id}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      className={cx(
        'relative',
        fullWidth ? 'flex w-full' : 'inline-flex max-w-full',
        isPill
          ? onDark
            ? 'rounded-full bg-white/10 p-1 ring-1 ring-inset ring-white/15'
            : 'rounded-full bg-[var(--pv-color-surface-sunken)] p-1 ring-1 ring-inset ring-[#DDEEEB] dark:ring-[var(--pv-border-soft)]'
          : onDark
            ? 'gap-1 border-b border-white/15'
            : 'gap-1 border-b border-[var(--pv-border-soft)]',
        className,
      )}
    >
      {options.map((option, index) => {
        const checked = index === checkedIndex;
        const off = isOff(index);
        return (
          <button
            key={option.value || `option-${index}`}
            ref={(el) => {
              buttonsRef.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={index === tabStop ? 0 : -1}
            disabled={off}
            onClick={() => select(index, false)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cx(
              'relative inline-flex items-center justify-center whitespace-nowrap leading-tight transition-colors duration-200',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
              onDark ? 'focus-visible:outline-white' : 'focus-visible:outline-[var(--pv-color-primary)]',
              'disabled:cursor-not-allowed disabled:opacity-50',
              s.gap,
              fullWidth && 'flex-1',
              isPill ? cx('rounded-full', s.pill) : cx('-mb-px', s.underline),
              onDark
                ? checked
                  ? isPill
                    ? 'font-semibold text-[#003930]'
                    : 'font-semibold text-white'
                  : 'text-white/75 hover:text-white'
                : checked
                  ? isPill
                    ? 'text-white dark:text-[var(--pv-color-surface)]'
                    : 'text-[var(--pv-color-primary)]'
                  : 'text-[var(--pv-color-text-muted)] hover:text-[var(--pv-color-primary)]',
            )}
          >
            {checked && (
              <motion.span
                layoutId={`segmented-${groupId}`}
                transition={reduce ? { duration: 0 } : SPRING}
                aria-hidden="true"
                className={
                  isPill
                    ? onDark
                      ? 'absolute inset-0 rounded-full bg-white'
                      : 'absolute inset-0 rounded-full bg-[var(--pv-color-primary)]'
                    : onDark
                      ? 'absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-[#FF8A80]'
                      : 'absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-[#DF2F2B]'
                }
              />
            )}
            {option.icon && (
              <span className={cx('relative z-10 shrink-0 leading-none', s.icon)} aria-hidden="true">
                {typeof option.icon === 'string' ? <i className={option.icon} /> : option.icon}
              </span>
            )}
            <span className="relative z-10">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
